import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { formBuilderService } from "../services/formBuilderService";
import {
  flattenFolderStructure,
  handleBeforeUnload,
} from "../utils/formBuilderUtils";

export const useFormBuilderLogic = (isDirty, setIsDirty) => {
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  // States
  const [folders, setFolders] = useState([]);
  const [folderOptions, setFolderOptions] = useState([]);
  const [selectedFolderIds, setSelectedFolderIds] = useState([]);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [shareDialog, setShareDialog] = useState(false);
  const [formLink, setFormLink] = useState("");
  const [backgroundPdf, setBackgroundPdf] = useState(null);
  const [showPdfBackground, setShowPdfBackground] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [calculationValues, setCalculationValues] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Cleanup effect for beforeunload
  useEffect(() => {
    if (isDirty) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // Fetch folders on mount
  useEffect(() => {
    fetchFolders();
  }, []);

  // Folder operations
  const fetchFolders = async () => {
    try {
      const result = await formBuilderService.fetchFolders(appToken, userData);
      setFolders(result.folders);

      if (result.formCategories && result.formCategories.formCategories) {
        const flattenedFolders = flattenFolderStructure(result.formCategories);
        setFolderOptions(flattenedFolders);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  // Form save operation
  const handleSaveForm = async (formData) => {
    // Onay modalını aç
    setSaveConfirmationOpen(true);
  };

  // Onaylandıktan sonra gerçek kaydetme işlemi
  const confirmSaveForm = async (formData) => {
    setIsSaving(true);
    setSaveConfirmationOpen(false); // Modal'ı kapat
    try {
      const saveData = {
        ...formData,
        selectedFolderIds,
        formLink,
        calculationValues,
      };

      await formBuilderService.saveForm(saveData, appToken, userData);

      setSnackbar({
        open: true,
        message: "Form başarıyla kaydedildi",
        severity: "success",
      });
      setIsDirty(false);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      // Kısa bir gecikme ile yönlendir
      setTimeout(() => {
        window.location.href = "/form-builder";
      }, 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Kaydetme onay modalı işlemleri
  const handleSaveConfirmationClose = () => {
    if (!isSaving) {
      setSaveConfirmationOpen(false);
    }
  };

  // Snackbar operations
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Folder dialog operations
  const handleFolderDialogOpen = () => {
    setFolderDialogOpen(true);
  };

  const handleFolderDialogClose = () => {
    setFolderDialogOpen(false);
  };

  // Copy link operation
  const handleCopyLink = () => {
    navigator.clipboard.writeText(formLink);
    showSnackbar("Bağlantı kopyalandı");
  };

  // Preview operations
  const handlePreviewOpen = () => {
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
  };

  const handleDeviceChange = (device) => {
    setPreviewDevice(device);
  };

  // Menu operations
  const handleMenuOpen = (event, pageId) => {
    setAnchorEl(event.currentTarget);
    setSelectedPageId(pageId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPageId(null);
  };

  // Calculation operations
  const handleCalculationChange = (fieldId, value) => {
    setCalculationValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  // PDF operations
  const handlePdfBackgroundLoad = (pdfData) => {
    setBackgroundPdf(pdfData);
    setShowPdfBackground(true);
  };

  return {
    // States
    folders,
    folderOptions,
    selectedFolderIds,
    setSelectedFolderIds,
    folderDialogOpen,
    snackbar,
    shareDialog,
    setShareDialog,
    formLink,
    setFormLink,
    backgroundPdf,
    showPdfBackground,
    previewModalOpen,
    previewDevice,
    calculationValues,
    anchorEl,
    selectedPageId,
    saveConfirmationOpen,
    isSaving,

    // Operations
    handleSaveForm,
    confirmSaveForm,
    handleSaveConfirmationClose,
    fetchFolders,
    showSnackbar,
    closeSnackbar,
    handleFolderDialogOpen,
    handleFolderDialogClose,
    handleCopyLink,
    handlePreviewOpen,
    handlePreviewClose,
    handleDeviceChange,
    handleMenuOpen,
    handleMenuClose,
    handleCalculationChange,
    handlePdfBackgroundLoad,
  };
};
